import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Box,
  IconButton,
  Tooltip,
  Button,
  TextField,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Close';
import { compareAmis } from '../services/api';
import type { FileDiff } from '../types/api';

interface CompareDiffAccordionProps {
  childId: string;
  parentId: string;
}

const CompareDiffAccordion: React.FC<CompareDiffAccordionProps> = ({ childId, parentId }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diffs, setDiffs] = useState<FileDiff[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: 'hash' | 'relativePath' | 'modifiedTimestamp'; direction: 'asc' | 'desc' }>({ key: 'relativePath', direction: 'asc' });
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);

  // Filtering state
  const [filters, setFilters] = useState<string[]>([
    '/var/lib/cloud',
    '/etc/ssh/ssh_host',
    '/var/log/',
    '/etc/hostname',
  ]);
  const [filterInput, setFilterInput] = useState<string>('');
  const [includeFilters, setIncludeFilters] = useState<string[]>([]);
  const [includeFilterInput, setIncludeFilterInput] = useState<string>('');

  // Auto-exclusions function
  const addAutoExclusions = () => {
    const autoExclusions = [
      '/tmp/',
      '/var/cache/',
      '/var/lib/apt/',
      '/var/lib/dpkg/',
      '/var/lib/yum/',
      '/proc/',
      '/sys/',
      '/dev/',
      '/run/',
      '/boot/',
      '/lib/modules/',
      '/usr/share/doc/',
      '/usr/share/man/',
      '/var/lib/systemd/',
      '/etc/machine-id',
      '/var/lib/dbus/machine-id',
      '/.dockerenv',
      '/var/lib/docker/',
      '/root/.bash_history',
      '/home/',
      '/var/spool/',
      '/var/mail/',
    ];
    
    setFilters(prev => {
      const newFilters = [...prev];
      autoExclusions.forEach(exclusion => {
        if (!newFilters.includes(exclusion)) {
          newFilters.push(exclusion);
        }
      });
      return newFilters;
    });
  };

  // Helper function to check if error is retryable (timeout/server errors)
  const isRetryableError = (status?: number): boolean => {
    return status === 503 || status === 504 || status === 502 || status === 408;
  };

  // Sleep function for delays
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Fetch compare data with retry logic
  const fetchCompareData = async (attempt: number = 0): Promise<void> => {
    try {
      const response = await compareAmis(childId, parentId);

      if (response.error && isRetryableError(response.status) && attempt < 2) {
        // Auto-retry with exponential backoff (attempt 1: 2s, attempt 2: 4s)
        const delay = Math.pow(2, attempt + 1) * 1000;
        setIsRetrying(true);
        setRetryCount(attempt + 1);
        await sleep(delay);
        return fetchCompareData(attempt + 1);
      }

      if (response.error) {
        setError(response.error);
        setDiffs([]);
      } else if (response.data === null || response.data === undefined) {
        // Handle null/undefined response from API (parent AMI not in database)
        setError('One or both AMIs not found in the database. Cannot compare AMIs that have not been scanned.');
        setDiffs([]);
      } else {
        setDiffs(response.data);
        setRetryCount(0); // Reset retry count on success
      }
    } catch {
      if (attempt < 2) {
        // Auto-retry on network errors
        const delay = Math.pow(2, attempt + 1) * 1000;
        setIsRetrying(true);
        setRetryCount(attempt + 1);
        await sleep(delay);
        return fetchCompareData(attempt + 1);
      }
      setError('Failed to fetch file differences');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleToggle = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded && diffs.length === 0 && !loading) {
      setLoading(true);
      setError(null);
      setRetryCount(0);
      
      await fetchCompareData();
      
      setLoading(false);
    }
  };

  // Manual retry function
  const handleManualRetry = async () => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
    
    await fetchCompareData();
    
    setLoading(false);
  };

  // Filter handler functions
  const handleFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filterInput.trim()) {
      e.preventDefault();
      setFilters((prev) => [...prev, filterInput.trim()]);
      setFilterInput('');
    }
  };

  const handleRemoveFilter = (value: string) => {
    setFilters((prev) => prev.filter((f) => f !== value));
  };

  const handleIncludeFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && includeFilterInput.trim()) {
      e.preventDefault();
      setIncludeFilters((prev) => [...prev, includeFilterInput.trim()]);
      setIncludeFilterInput('');
    }
  };

  const handleRemoveIncludeFilter = (value: string) => {
    setIncludeFilters((prev) => prev.filter((f) => f !== value));
  };

  const sortedFilteredDiffs = (() => {
    // Safety check: ensure diffs is an array
    if (!Array.isArray(diffs)) {
      return [];
    }

    const filtered = diffs.filter((diff) => {
      // First apply exclude filters (must not match any exclude filter)
      const excludeMatch = filters.some((f) =>
        diff.hash.toLowerCase().includes(f.toLowerCase()) ||
        diff.relativePath.toLowerCase().includes(f.toLowerCase())
      );

      if (excludeMatch) return false;

      // Then apply include filters (if any exist, must match at least one include filter)
      if (includeFilters.length > 0) {
        const includeMatch = includeFilters.some((f) =>
          diff.hash.toLowerCase().includes(f.toLowerCase()) ||
          diff.relativePath.toLowerCase().includes(f.toLowerCase())
        );
        return includeMatch;
      }

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortConfig.key === 'modifiedTimestamp') {
        const aTime = a.modifiedTimestamp ? new Date(a.modifiedTimestamp).getTime() : 0;
        const bTime = b.modifiedTimestamp ? new Date(b.modifiedTimestamp).getTime() : 0;
        if (aTime < bTime) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aTime > bTime) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      }
      const aVal = a[sortConfig.key].toLowerCase();
      const bVal = b[sortConfig.key].toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  })();

  const handleSort = (key: 'hash' | 'relativePath' | 'modifiedTimestamp') => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  return (
    <Accordion expanded={expanded} onChange={handleToggle} sx={{ bgcolor: 'background.default' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '.MuiAccordionSummary-content': {
            flexGrow: 1,
            justifyContent: 'center',
          },
        }}
      >
        <Typography variant="subtitle2">
          Click here to view all files on the child AMI filesystem that have been modified since it was cloned from the parent AMI
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {loading || isRetrying ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            {isRetrying && (
              <Typography variant="body2" color="text.secondary">
                Request timed out. Retrying... (Attempt {retryCount}/2)
              </Typography>
            )}
          </Box>
        ) : error ? (
          <Box sx={{ width: '100%' }}>
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                isRetryableError(parseInt(error.match(/\d{3}/)?.[0] || '0')) ? (
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleManualRetry}
                    disabled={loading}
                  >
                    Retry
                  </Button>
                ) : null
              }
            >
              {error.includes('404') ? 'One or both AMIs not found in the database' : error}
              {isRetryableError(parseInt(error.match(/\d{3}/)?.[0] || '0')) && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This operation sometimes takes longer than expected. Click "Retry" to try again.
                </Typography>
              )}
            </Alert>
          </Box>
        ) : diffs.length === 0 ? (
          <Alert severity="info">No modified files found</Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            {/* Filter Controls - matching AMIDetails styling */}
            <Box sx={{ mb: 2 }}>
              {/* Exclude Filters */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <TextField
                  label="Exclude (press Enter)"
                  variant="outlined"
                  size="small"
                  value={filterInput}
                  onChange={(e) => setFilterInput(e.target.value)}
                  onKeyDown={handleFilterKeyDown}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={addAutoExclusions}
                  sx={{ minWidth: 'auto' }}
                >
                  + Auto Exclusions
                </Button>
                {filters.map((f) => (
                  <Chip
                    key={f}
                    label={f}
                    onDelete={() => handleRemoveFilter(f)}
                    deleteIcon={<DeleteIcon />}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
              {/* Include Filters */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <TextField
                  label="Show only (press Enter)"
                  variant="outlined"
                  size="small"
                  value={includeFilterInput}
                  onChange={(e) => setIncludeFilterInput(e.target.value)}
                  onKeyDown={handleIncludeFilterKeyDown}
                  helperText="Filter to show only files matching these paths"
                />
                {includeFilters.map((f) => (
                  <Chip
                    key={f}
                    label={f}
                    onDelete={() => handleRemoveIncludeFilter(f)}
                    deleteIcon={<DeleteIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Differing Files ({sortedFilteredDiffs.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '80px', padding: '6px 8px', fontSize: '0.75rem' }}>
                    Actions
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 'auto', padding: '6px 8px', fontSize: '0.75rem' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'relativePath'}
                      direction={sortConfig.key === 'relativePath' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('relativePath')}
                    >
                      Relative Path
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '180px', padding: '6px 8px', fontSize: '0.75rem' }}>
                    <TableSortLabel
                      active={sortConfig.key === 'modifiedTimestamp'}
                      direction={
                        sortConfig.key === 'modifiedTimestamp' ? sortConfig.direction : 'asc'
                      }
                      onClick={() => handleSort('modifiedTimestamp')}
                    >
                      Modified Timestamp
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedFilteredDiffs.map((diff, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ width: '80px', padding: '6px 8px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={`Copy hash: ${diff.hash}`}>
                          <IconButton
                            size="small"
                            onClick={() => navigator.clipboard.writeText(diff.hash)}
                            aria-label="Copy hash"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={`Lookup on VirusTotal: ${diff.hash}`}>
                          <IconButton
                            size="small"
                            component="a"
                            href={`https://www.virustotal.com/gui/file/${diff.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Lookup on VirusTotal"
                          >
                            <SearchIcon fontSize="small" sx={{ color: '#4B8DF8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', width: 'auto', padding: '6px 8px', fontSize: '0.75rem' }}>
                      {diff.relativePath}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', width: '180px', padding: '6px 8px', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      {new Date(diff.modifiedTimestamp).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CompareDiffAccordion; 